import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Briefcase,
  Edit,
  Upload,
  Clock,
  ChevronRight,
  Building,
  GraduationCap,
  Code,
  Heart,
  DollarSign,
  AlertCircle,
  Save,
} from 'lucide-react';
import { getCandidateProfileStats, getCandidateProfile } from '@/services/api';
import { profileService } from '@/services/profileService';
import Card from '@/components/common/Card';
import ProfileCompletionWidget from '@/components/candidate/ProfileCompletionWidget';
import PublicProfileWidget from '@/components/candidate/PublicProfileWidget';
import { useTranslation } from 'react-i18next';
import { candidateJobOfferService } from '@/services/jobOfferService';
import type { JobOffer, Candidate } from '@/types';
import { useAuth } from '@/stores/authStore';


export default function CandidateDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation('dashboard');
  const queryClient = useQueryClient();

  const [jobOffers, setJobOffers] = useState<JobOffer[]>([]);
  const [jobOffersLoading, setJobOffersLoading] = useState(true);
  const [pendingOffersCount, setPendingOffersCount] = useState(0);
  const [offerStats, setOfferStats] = useState<{ total: number; accepted: number; rejected: number } | null>(null);
  const [savingPreference, setSavingPreference] = useState(false);

  // Salary & education require local state + explicit save
  const [salaryMin, setSalaryMin] = useState<number | null>(null);
  const [salaryMax, setSalaryMax] = useState<number | null>(null);
  const [educationYears, setEducationYears] = useState<number | null>(null);
  const [salaryError, setSalaryError] = useState<string | null>(null);
  const [educationError, setEducationError] = useState<string | null>(null);
  const [isSavingFields, setIsSavingFields] = useState(false);

  const enabled = user?.dataProcessingConsent === true;

  // Profile stats via TanStack Query — shared cache with ProfileCompletionWidget
  const { data: statsData } = useQuery({
    queryKey: ['candidateProfileStats'],
    queryFn: getCandidateProfileStats,
    enabled,
    staleTime: 0,
  });

  // Candidate profile for employment status & relocation preference
  const { data: candidateProfile } = useQuery({
    queryKey: ['candidateProfile'],
    queryFn: getCandidateProfile,
    enabled,
  });

  useEffect(() => {
    if (!enabled) {
      setJobOffersLoading(false);
      return;
    }
    fetchJobOffers();
  }, [enabled]);

  // Sync salary & education from profile cache whenever it loads/changes
  useEffect(() => {
    setSalaryMin(candidateProfile?.salaryMin ?? null);
    setSalaryMax(candidateProfile?.salaryMax ?? null);
    setEducationYears(candidateProfile?.educationYears ?? null);
  }, [candidateProfile]);

  const fetchJobOffers = async () => {
    try {
      const response = await candidateJobOfferService.listJobOffers({
        status: ['PENDING', 'VIEWED', 'EXPIRED'],
        limit: 3,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      });
      setJobOffers(response.offers);
      setPendingOffersCount(response.stats.pending);
      setOfferStats({
        total: response.stats.total,
        accepted: response.stats.accepted,
        rejected: response.stats.rejected,
      });
    } catch {
      // Silently fail
    } finally {
      setJobOffersLoading(false);
    }
  };

  const handleStatusChange = async (value: string) => {
    if (savingPreference) return;
    const next = value === '' ? null : (value as Candidate['employmentStatus']);
    setSavingPreference(true);
    queryClient.setQueryData(['candidateProfile'], (old: Candidate | undefined) =>
      old ? { ...old, employmentStatus: next } : old
    );
    try {
      await profileService.updateCandidateProfile({ employmentStatus: next });
    } catch {
      queryClient.invalidateQueries({ queryKey: ['candidateProfile'] });
    } finally {
      setSavingPreference(false);
    }
  };

  const handleContractChange = async (value: string) => {
    if (savingPreference) return;
    const next = value === '' ? null : (value as Candidate['contractType']);
    setSavingPreference(true);
    queryClient.setQueryData(['candidateProfile'], (old: Candidate | undefined) =>
      old ? { ...old, contractType: next } : old
    );
    try {
      await profileService.updateCandidateProfile({ contractType: next });
    } catch {
      queryClient.invalidateQueries({ queryKey: ['candidateProfile'] });
    } finally {
      setSavingPreference(false);
    }
  };

  const handleRelocationChange = async (value: string) => {
    if (savingPreference) return;
    const next = value === '' ? null : value === 'true';
    setSavingPreference(true);
    queryClient.setQueryData(['candidateProfile'], (old: Candidate | undefined) =>
      old ? { ...old, willingToRelocate: next } : old
    );
    try {
      if (next !== null) {
        await profileService.updateCandidateProfile({ willingToRelocate: next });
      }
    } catch {
      queryClient.invalidateQueries({ queryKey: ['candidateProfile'] });
    } finally {
      setSavingPreference(false);
    }
  };

  const handleSaveFields = async () => {
    if (isSavingFields) return;
    let hasError = false;
    const SALARY_MAX_LIMIT = 30000;
    if (
      (salaryMin !== null && (salaryMin < 0 || salaryMin > SALARY_MAX_LIMIT)) ||
      (salaryMax !== null && (salaryMax < 0 || salaryMax > SALARY_MAX_LIMIT))
    ) {
      setSalaryError(t('candidate.jobPreferences.salary.rangeError'));
      hasError = true;
    } else if (salaryMin !== null && salaryMax !== null && salaryMax < salaryMin) {
      setSalaryError(t('candidate.jobPreferences.salary.error'));
      hasError = true;
    } else {
      setSalaryError(null);
    }
    if (educationYears !== null && (educationYears < 0 || educationYears > 15)) {
      setEducationError(t('candidate.jobPreferences.education.error'));
      hasError = true;
    } else {
      setEducationError(null);
    }
    if (hasError) return;

    const savedMin = candidateProfile?.salaryMin ?? null;
    const savedMax = candidateProfile?.salaryMax ?? null;
    const savedEdu = candidateProfile?.educationYears ?? null;
    const unchanged = salaryMin === savedMin && salaryMax === savedMax && educationYears === savedEdu;
    if (unchanged) return;

    const clamp = (v: number | null, min: number, max: number) =>
      v === null ? null : Math.min(Math.max(v, min), max);
    const safeSalaryMin = clamp(salaryMin, 0, 30000);
    const safeSalaryMax = clamp(salaryMax, 0, 30000);

    setIsSavingFields(true);
    queryClient.setQueryData(['candidateProfile'], (old: Candidate | undefined) =>
      old ? { ...old, salaryMin: safeSalaryMin, salaryMax: safeSalaryMax, educationYears } : old
    );
    try {
      await profileService.updateCandidateProfile({ salaryMin: safeSalaryMin, salaryMax: safeSalaryMax, educationYears });
    } catch {
      queryClient.invalidateQueries({ queryKey: ['candidateProfile'] });
    } finally {
      setIsSavingFields(false);
    }
  };

  const sections = statsData?.sections ?? [];
  const skillsCount = statsData?.skillsCount;

  // Derive quick-action context from sections
  const experienceSection = sections.find(
    (s) => s.id === 'work_experience' || s.id === 'experience'
  );
  const educationSection = sections.find((s) => s.id === 'education');
  const documentsSection = sections.find((s) => s.id === 'documents' || s.id === 'cv');

  const hasExperience = experienceSection?.completedFields ?? 0;
  const hasEducation = educationSection?.completedFields ?? 0;
  const hasCV = documentsSection?.completed ?? false;

  const experienceLevel = candidateProfile?.experienceLevel ?? null;

  return (
    <div className="container-custom py-8 space-y-6">
      {/* Welcome Section */}
      <div className="animate-slide-down">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">
          {t('candidate.welcome', { name: user?.firstName })}
        </h1>
        <p className="text-gray-500 text-sm">{t('candidate.subtitle')}</p>
      </div>

      {/* Public Portfolio Widget */}
      <div className="animate-slide-up">
        <PublicProfileWidget username={user?.username} />
      </div>

      {/* Profile Completion Widget */}
      <div className="animate-slide-up">
        <ProfileCompletionWidget />
      </div>

      {/* Job Preferences Control Panel */}
      <Card className="p-6 animate-slide-up">
        {/* Header */}
        <div className="flex items-center gap-2 mb-5">
          <div className="p-2 rounded-lg bg-blue-50">
            <Heart className="w-4 h-4 text-blue-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              {t('candidate.jobPreferences.title')}
            </h3>
            <p className="text-xs text-gray-500">{t('candidate.jobPreferences.subtitle')}</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          {/* Availability */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              {t('candidate.jobPreferences.status.label')}
            </label>
            <select
              value={candidateProfile?.employmentStatus ?? ''}
              onChange={(e) => handleStatusChange(e.target.value)}
              disabled={savingPreference}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">{t('candidate.jobPreferences.status.placeholder')}</option>
              <option value="ACTIVELY_LOOKING">{t('candidate.jobPreferences.status.ACTIVELY_LOOKING')}</option>
              <option value="OPEN_TO_OFFERS">{t('candidate.jobPreferences.status.OPEN_TO_OFFERS')}</option>
              <option value="NOT_LOOKING">{t('candidate.jobPreferences.status.NOT_LOOKING')}</option>
            </select>
          </div>

          {/* Contract Type */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              {t('candidate.jobPreferences.contractType.label')}
            </label>
            <select
              value={candidateProfile?.contractType ?? ''}
              onChange={(e) => handleContractChange(e.target.value)}
              disabled={savingPreference}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">{t('candidate.jobPreferences.contractType.placeholder')}</option>
              <option value="FULL_TIME">{t('candidate.jobPreferences.contractType.FULL_TIME')}</option>
              <option value="HALF_TIME">{t('candidate.jobPreferences.contractType.HALF_TIME')}</option>
              <option value="CONTRACTOR">{t('candidate.jobPreferences.contractType.CONTRACTOR')}</option>
              <option value="INTERNSHIP">{t('candidate.jobPreferences.contractType.INTERNSHIP')}</option>
              <option value="PFE">{t('candidate.jobPreferences.contractType.PFE')}</option>
            </select>
          </div>

          {/* Willing to Relocate */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              {t('candidate.jobPreferences.relocation.label')}
            </label>
            <select
              value={
                candidateProfile?.willingToRelocate == null
                  ? ''
                  : candidateProfile.willingToRelocate
                  ? 'true'
                  : 'false'
              }
              onChange={(e) => handleRelocationChange(e.target.value)}
              disabled={savingPreference}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">{t('candidate.jobPreferences.relocation.placeholder')}</option>
              <option value="true">{t('candidate.jobPreferences.relocation.yes')}</option>
              <option value="false">{t('candidate.jobPreferences.relocation.no')}</option>
            </select>
          </div>

          {/* Experience Level (read-only) */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              {t('candidate.jobPreferences.experience.label')}
            </label>
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50">
              <Briefcase className="w-4 h-4 text-gray-400 flex-shrink-0" />
              {experienceLevel ? (
                <span className="text-sm text-gray-700 font-medium">
                  {t('candidate.jobPreferences.experience.years', { level: experienceLevel })}
                </span>
              ) : (
                <span className="text-sm text-gray-400">
                  {t('candidate.jobPreferences.experience.noExperience')}
                </span>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-400">
              {t('candidate.jobPreferences.experience.autoCalculated')}
            </p>
          </div>
        </div>

        <div className="mt-5 pt-5 border-t border-gray-100">
          <div className="grid sm:grid-cols-2 gap-5">
            {/* Salary Range */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">
                <DollarSign className="w-3.5 h-3.5 inline mr-0.5 text-gray-500" />
                {t('candidate.jobPreferences.salary.label')}
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="0"
                  max={30000}
                  value={salaryMin ?? ''}
                  onChange={(e) => {
                    const v = e.target.value === '' ? null : parseInt(e.target.value, 10);
                    setSalaryMin(v);
                    setSalaryError(null);
                  }}
                  placeholder={t('candidate.jobPreferences.salary.minPlaceholder')}
                  className="w-0 flex-1 px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                  disabled={isSavingFields}
                />
                <input
                  type="number"
                  min="0"
                  max={30000}
                  value={salaryMax ?? ''}
                  onChange={(e) => {
                    const v = e.target.value === '' ? null : parseInt(e.target.value, 10);
                    setSalaryMax(v);
                    setSalaryError(null);
                  }}
                  placeholder={t('candidate.jobPreferences.salary.maxPlaceholder')}
                  className={`w-0 flex-1 px-3 py-2 rounded-lg border bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                    salaryError ? 'border-red-400' : 'border-gray-200'
                  }`}
                  disabled={isSavingFields}
                />
              </div>
              {salaryError && (
                <p className="mt-1.5 flex items-center gap-1 text-xs text-red-600">
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                  {salaryError}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-400">
                {t('candidate.jobPreferences.salary.currency')}
              </p>
            </div>

            {/* Education Level */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">
                <GraduationCap className="w-3.5 h-3.5 inline mr-0.5 text-gray-500" />
                {t('candidate.jobPreferences.education.yearsLabel')}
              </label>
              <input
                type="number"
                min="0"
                max="15"
                value={educationYears ?? ''}
                onChange={(e) => {
                  const v = e.target.value === '' ? null : parseInt(e.target.value, 10);
                  setEducationYears(v);
                  setEducationError(null);
                }}
                placeholder={t('candidate.jobPreferences.education.placeholder')}
                className={`w-full px-3 py-2 rounded-lg border bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                  educationError ? 'border-red-400' : 'border-gray-200'
                }`}
                disabled={isSavingFields}
              />
              {educationError && (
                <p className="mt-1.5 flex items-center gap-1 text-xs text-red-600">
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                  {educationError}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-400">
                {t('candidate.jobPreferences.education.hint')}
              </p>
            </div>
          </div>

          {/* Single save button */}
          <div className="mt-4 flex justify-end">
            <button
              onClick={handleSaveFields}
              disabled={isSavingFields}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-3.5 h-3.5" />
              {isSavingFields ? t('candidate.jobPreferences.saving') : t('candidate.jobPreferences.salary.save')}
            </button>
          </div>
        </div>

        {/* Skills */}
        {skillsCount != null && (
          <div className="mt-5 pt-5 border-t border-gray-100">
            <label className="block text-xs font-medium text-gray-600 mb-2">
              {t('candidate.jobPreferences.skills.label')}
            </label>
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-white border border-purple-200 text-purple-700">
                <Code className="w-3.5 h-3.5" />
                {t('candidate.jobPreferences.skills.count', { count: skillsCount })}
              </span>
              {skillsCount < 5 && (
                <p className="text-xs text-amber-600">
                  {t('candidate.jobPreferences.skills.hint')}{' '}
                  <button
                    onClick={() => navigate('/candidate/profile/edit#skills')}
                    className="font-medium underline underline-offset-2 hover:text-amber-700 transition-colors"
                  >
                    {t('candidate.jobPreferences.skills.addLink')}
                  </button>
                </p>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* Job Offers Widget */}
      {!jobOffersLoading && (
        <Card className="p-6 animate-slide-up">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-purple-500">
                <Briefcase className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">
                  {t('candidate.jobOffers.title')}
                </h3>
                {pendingOffersCount > 0 && (
                  <p className="text-xs text-blue-600 font-medium">
                    {t('candidate.jobOffers.pending', { count: pendingOffersCount })}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={() => navigate('/candidate/job-offers')}
              className="flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors"
            >
              {t('candidate.jobOffers.viewAll')}
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Offer stats row */}
          {offerStats && offerStats.total > 0 && (
            <div className="flex gap-3 mb-3 pb-3 border-b border-gray-100 text-xs text-gray-500">
              <span>{t('candidate.jobOffers.stats.total', { count: offerStats.total })}</span>
              <span className="text-green-600">
                {t('candidate.jobOffers.stats.accepted', { count: offerStats.accepted })}
              </span>
              <span className="text-red-500">
                {t('candidate.jobOffers.stats.rejected', { count: offerStats.rejected })}
              </span>
            </div>
          )}

          {jobOffers.length > 0 ? (
            <div className="space-y-2">
              {jobOffers.map((offer) => (
                <div
                  key={offer.id}
                  onClick={() => navigate(`/candidate/job-offers/${offer.id}`)}
                  className="flex items-start gap-3 p-3 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 cursor-pointer transition-colors"
                >
                  <div className="p-1.5 rounded-lg bg-blue-100 flex-shrink-0">
                    <Building className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="font-semibold text-gray-900 truncate text-sm">{offer.position}</p>
                      {offer.status === 'PENDING' && (
                        <span className="px-1.5 py-0.5 text-xs font-medium text-white bg-blue-600 rounded">
                          {t('candidate.jobOffers.newOffer')}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 truncate">{offer.companyName}</p>
                    {offer.location && (
                      <p className="text-xs text-gray-500 mt-0.5">{offer.location}</p>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <Clock className="w-10 h-10 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-600 text-sm">{t('candidate.jobOffers.empty')}</p>
              <p className="text-gray-500 text-xs mt-1">
                {t('candidate.jobOffers.emptyDescription')}
              </p>
            </div>
          )}
        </Card>
      )}

      {/* Quick Actions */}
      <div className="animate-slide-up delay-200">
        <h2 className="text-lg font-bold text-gray-900 mb-3">
          {t('candidate.quickActions.title')}
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Edit Profile */}
          <Card
            className="p-4 hover:shadow-md transition-shadow cursor-pointer group"
            onClick={() => navigate('/candidate/profile/edit')}
          >
            <div className="p-2 rounded-xl bg-blue-100 group-hover:bg-blue-200 transition-colors mb-3 w-fit">
              <Edit className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900 mb-1">
              {t('candidate.quickActions.editProfile.title')}
            </h3>
            <p className="text-xs text-gray-600">
              {t('candidate.quickActions.editProfile.description')}
            </p>
          </Card>

          {/* Add / Manage Experience */}
          <Card
            className="p-4 hover:shadow-md transition-shadow cursor-pointer group"
            onClick={() => navigate('/candidate/profile/edit#experience')}
          >
            <div className="p-2 rounded-xl bg-purple-100 group-hover:bg-purple-200 transition-colors mb-3 w-fit">
              <Briefcase className="w-5 h-5 text-purple-600" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900 mb-1">
              {hasExperience > 0
                ? t('candidate.quickActions.experience.titleManage')
                : t('candidate.quickActions.experience.titleAdd')}
            </h3>
            <p className="text-xs text-gray-600">
              {hasExperience > 0
                ? t('candidate.quickActions.experience.count', { count: hasExperience })
                : t('candidate.quickActions.experience.descriptionAdd')}
            </p>
          </Card>

          {/* Add / Manage Education */}
          <Card
            className="p-4 hover:shadow-md transition-shadow cursor-pointer group"
            onClick={() => navigate('/candidate/profile/edit#education')}
          >
            <div className="p-2 rounded-xl bg-green-100 group-hover:bg-green-200 transition-colors mb-3 w-fit">
              <GraduationCap className="w-5 h-5 text-green-600" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900 mb-1">
              {hasEducation > 0
                ? t('candidate.quickActions.education.titleManage')
                : t('candidate.quickActions.education.titleAdd')}
            </h3>
            <p className="text-xs text-gray-600">
              {hasEducation > 0
                ? t('candidate.quickActions.education.count', { count: hasEducation })
                : t('candidate.quickActions.education.descriptionAdd')}
            </p>
          </Card>

          {/* Upload / Update CV */}
          <Card
            className="p-4 hover:shadow-md transition-shadow cursor-pointer group"
            onClick={() => navigate('/candidate/profile/edit#documents')}
          >
            <div className="p-2 rounded-xl bg-yellow-100 group-hover:bg-yellow-200 transition-colors mb-3 w-fit">
              <Upload className="w-5 h-5 text-yellow-600" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900 mb-1">
              {hasCV
                ? t('candidate.quickActions.cv.titleUpdate')
                : t('candidate.quickActions.cv.titleUpload')}
            </h3>
            <p className="text-xs text-gray-600">
              {hasCV
                ? t('candidate.quickActions.cv.descriptionUpdate')
                : t('candidate.quickActions.cv.descriptionAdd')}
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
}
